type Texts = {
    remove: string;
    remaining: string;
    count: string;
    errorCount: string;
    errorSize: string;
    errorTooLarge: string;
    errorType: string;
    errorDuplicate: string;
    errorEmpty: string;
};

/**
 * Attachments of the inquiry form: files can be picked or dropped in several rounds,
 * they are listed in a grid and kept within the count and total size limits.
 *
 * The picked files always end up in the native <input type="file" multiple>, so Naja
 * and the non-AJAX fallback send them with the rest of the form.
 */
export default class FileUpload {
    private readonly input: HTMLInputElement;

    private readonly list: HTMLElement;

    private readonly errors: HTMLElement;

    private readonly maxCount: number;

    private readonly maxSize: number;

    private readonly extensions: string[];

    private readonly texts: Texts;

    private readonly numberFormat: Intl.NumberFormat;

    private files: File[] = [];

    private dragDepth = 0;

    static init(): void {
        document.querySelectorAll<HTMLElement>('[data-file-upload]').forEach((element) => {
            if (!element.dataset.fileUploadReady) {
                element.dataset.fileUploadReady = '1';
                new FileUpload(element);
            }
        });
    }

    constructor(private readonly element: HTMLElement) {
        this.input = element.querySelector<HTMLInputElement>('.file-upload__input')!;
        this.list = element.querySelector<HTMLElement>('[data-file-upload-list]')!;
        this.errors = element.querySelector<HTMLElement>('[data-file-upload-errors]')!;
        this.maxCount = Number(element.dataset.maxCount);
        this.maxSize = Number(element.dataset.maxSize);
        this.extensions = (element.dataset.extensions ?? '').split(',').filter(Boolean);
        this.texts = JSON.parse(element.dataset.texts ?? '{}') as Texts;
        this.numberFormat = new Intl.NumberFormat(document.documentElement.lang || undefined, {
            maximumFractionDigits: 1,
        });

        this.input.addEventListener('change', () => {
            // the input holds just the new selection now, put it together with the earlier files
            this.add(Array.from(this.input.files ?? []));
        });

        element.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            if (target.closest('[data-file-upload-pick]')) {
                event.preventDefault();
                this.input.click();
                return;
            }

            const remove = target.closest<HTMLElement>('[data-file-upload-remove]');
            if (remove) {
                // detail is 0 for clicks made with the keyboard
                this.remove(Number(remove.dataset.fileUploadRemove), event.detail === 0);
            }
        });

        this.initDragAndDrop();
        this.render();
    }

    private initDragAndDrop(): void {
        const hasFiles = (event: DragEvent): boolean => Array.from(event.dataTransfer?.types ?? []).includes('Files');

        this.element.addEventListener('dragenter', (event) => {
            if (!hasFiles(event)) {
                return;
            }

            event.preventDefault();
            this.dragDepth += 1;
            this.element.classList.add('is-dragover');
        });

        this.element.addEventListener('dragover', (event) => {
            if (hasFiles(event)) {
                event.preventDefault();
                event.dataTransfer!.dropEffect = 'copy';
            }
        });

        // dragleave fires for every child the cursor crosses, count the depth to know when it really left
        this.element.addEventListener('dragleave', () => {
            this.dragDepth = Math.max(0, this.dragDepth - 1);
            if (this.dragDepth === 0) {
                this.element.classList.remove('is-dragover');
            }
        });

        this.element.addEventListener('drop', (event) => {
            event.preventDefault();
            this.dragDepth = 0;
            this.element.classList.remove('is-dragover');
            this.add(Array.from(event.dataTransfer?.files ?? []));
        });
    }

    private add(newFiles: File[]): void {
        const errors: string[] = [];
        let countReached = false;

        newFiles.forEach((file) => {
            const name = FileUpload.shorten(file.name, 40);

            if (!this.extensions.includes(FileUpload.extension(file.name))) {
                errors.push(FileUpload.format(this.texts.errorType, name));
            } else if (file.size === 0) {
                errors.push(FileUpload.format(this.texts.errorEmpty, name));
            } else if (this.files.some((added) => FileUpload.isSame(added, file))) {
                errors.push(FileUpload.format(this.texts.errorDuplicate, name));
            } else if (this.files.length >= this.maxCount) {
                countReached = true;
            } else if (file.size > this.maxSize) {
                errors.push(FileUpload.format(this.texts.errorTooLarge, name, this.formatSize(this.maxSize)));
            } else if (file.size > this.remainingSize()) {
                errors.push(FileUpload.format(this.texts.errorSize, name, this.formatSize(this.remainingSize())));
            } else {
                this.files.push(file);
            }
        });

        if (countReached) {
            errors.push(this.texts.errorCount);
        }

        this.sync();
        this.render(errors);
    }

    private remove(index: number, moveFocus: boolean): void {
        this.files.splice(index, 1);
        this.sync();
        this.render();

        // keep the keyboard focus inside the component after the button is gone
        if (moveFocus) {
            const focusable = this.list.querySelectorAll<HTMLElement>('button');
            (
                focusable[Math.min(index, focusable.length - 1)] ??
                this.element.querySelector<HTMLElement>('.file-upload__choose')
            )?.focus();
        }
    }

    /**
     * Puts all the kept files into the native input.
     */
    private sync(): void {
        try {
            const transfer = new DataTransfer();
            this.files.forEach((file) => transfer.items.add(file));
            this.input.files = transfer.files;
        } catch {
            // browsers without the DataTransfer constructor keep just the last selection
        }
    }

    private render(errors: string[] = []): void {
        const itemTemplate = this.element.querySelector<HTMLTemplateElement>('template[data-file-upload-item]')!;
        const addTemplate = this.element.querySelector<HTMLTemplateElement>('template[data-file-upload-add]')!;
        const fullTemplate = this.element.querySelector<HTMLTemplateElement>('template[data-file-upload-full]')!;

        this.list.replaceChildren();
        this.files.forEach((file, index) => {
            const item = itemTemplate.content.firstElementChild!.cloneNode(true) as HTMLElement;
            const extension = FileUpload.extension(file.name);
            const base = extension !== '' ? file.name.slice(0, -(extension.length + 1)) : file.name;

            item.querySelector('.file-upload__name')!.setAttribute('title', file.name);
            item.querySelector('.file-upload__name-base')!.textContent = base;
            item.querySelector('.file-upload__name-ext')!.textContent = extension !== '' ? `.${extension}` : '';
            item.querySelector('.file-upload__size')!.textContent = this.formatSize(file.size);

            const remove = item.querySelector<HTMLElement>('.file-upload__remove')!;
            remove.dataset.fileUploadRemove = String(index);
            remove.setAttribute('aria-label', FileUpload.format(this.texts.remove, file.name));
            remove.setAttribute('title', FileUpload.format(this.texts.remove, file.name));

            this.list.appendChild(item);
        });

        if (this.files.length > 0) {
            const canAdd = this.files.length < this.maxCount && this.remainingSize() > 0;
            this.list.appendChild((canAdd ? addTemplate : fullTemplate).content.firstElementChild!.cloneNode(true));
        }

        const used = this.usedSize();
        const ratio = this.maxSize > 0 ? used / this.maxSize : 0;
        const bar = this.element.querySelector<HTMLElement>('[data-file-upload-bar]')!;
        bar.style.width = `${Math.min(100, ratio * 100)}%`;
        this.element.classList.toggle('has-files', this.files.length > 0);
        this.element.classList.toggle('is-almost-full', ratio >= 0.9 || this.files.length >= this.maxCount);

        this.element.querySelector('[data-file-upload-remaining]')!.textContent = FileUpload.format(
            this.texts.remaining,
            this.formatSize(this.remainingSize()),
            this.formatSize(this.maxSize),
        );
        this.element.querySelector('[data-file-upload-count]')!.textContent = FileUpload.format(
            this.texts.count,
            String(this.files.length),
            String(this.maxCount),
        );

        this.errors.replaceChildren(
            ...errors.map((message) => {
                const item = document.createElement('li');
                item.className = 'form-error';
                item.textContent = message;
                return item;
            }),
        );
    }

    private usedSize(): number {
        return this.files.reduce((sum, file) => sum + file.size, 0);
    }

    private remainingSize(): number {
        return Math.max(0, this.maxSize - this.usedSize());
    }

    private formatSize(bytes: number): string {
        if (bytes < 1024) {
            return `${bytes} B`;
        }

        if (bytes < 1024 * 1024) {
            return `${this.numberFormat.format(bytes / 1024)} kB`;
        }

        return `${this.numberFormat.format(bytes / 1024 / 1024)} MB`;
    }

    private static extension(name: string): string {
        const dot = name.lastIndexOf('.');
        return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
    }

    private static isSame(a: File, b: File): boolean {
        return a.name === b.name && a.size === b.size;
    }

    /**
     * Middle ellipsis for names in error messages, the extension stays readable.
     */
    private static shorten(name: string, max: number): string {
        return name.length <= max ? name : `${name.slice(0, max - 12)}…${name.slice(-10)}`;
    }

    /**
     * Fills %s / %d placeholders of a translation in order.
     */
    private static format(message: string, ...values: string[]): string {
        let index = 0;
        return message.replace(/%[sd]/g, (match) => values[index++] ?? match);
    }
}
